import React from 'react';
import { RadioGroup, Radio, Icon } from '@ace-clublabs/shared-component-library';
import { CardFormValidationHelper } from '../../utils/cardFormValidationHelper';

export interface PaymentMethodOption {
  id: string;
  label: string;
  type: 'card' | 'apple-pay' | 'google-pay';
  available: boolean;
}

interface PaymentMethodSelectorProps {
  methods: PaymentMethodOption[];
  selectedMethod: string | null;
  onSelect: (id: string) => void;
  cardBrands?: string[];
}

const TYPE_ORDER: Record<PaymentMethodOption['type'], number> = {
  'apple-pay': 0,
  'google-pay': 1,
  card: 2,
};

const STANDARD_BRAND_TO_ICON: Record<string, string> = {
  americanexpress: 'amex-logo',
  mastercard: 'mastercard-logo',
  visa: 'visa-logo',
  discover: 'discover-logo',
};

const CARD_ICON_ORDER = ['amex-logo', 'mastercard-logo', 'visa-logo', 'discover-logo'];

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  methods,
  selectedMethod,
  onSelect,
  cardBrands,
}) => {
  const availableMethods = methods
    .filter((m) => m.available)
    .sort((a, b) => TYPE_ORDER[a.type] - TYPE_ORDER[b.type]);

  if (availableMethods.length === 0) {
    return null;
  }

  const renderLabel = (method: PaymentMethodOption) => {
    if (method.type === 'apple-pay') {
      return (
        <div className="payment-type apple-pay">
          <span>{method.label}</span>
          <Icon iconName={'apple-pay-logo' as any} />
        </div>
      );
    }
    if (method.type === 'google-pay') {
      return (
        <div className="payment-type google-pay">
          <span>{method.label}</span>
          <Icon iconName={'google-pay-logo' as any} />
        </div>
      );
    }
    // card
    const icons = cardBrands
      ? [...new Set(
          cardBrands
            .map((b) => STANDARD_BRAND_TO_ICON[CardFormValidationHelper.mapAdyenBrandToStandard(b)])
            .filter(Boolean),
        )].sort((a, b) => CARD_ICON_ORDER.indexOf(a) - CARD_ICON_ORDER.indexOf(b))
      : CARD_ICON_ORDER;
    return (
      <div className="payment-type card">
        <span>{method.label}</span>
        <span className="card-brand-icons">
          {icons.map((iconName) => (
            <Icon key={iconName} iconName={iconName as any} />
          ))}
        </span>
      </div>
    );
  };

  if (availableMethods.length === 1) {
    const method = availableMethods[0];
    return (
      <div className="payment-method-selector--single">
        {renderLabel(method)}
      </div>
    );
  }

  return (
    <>
      <span className="payment-method-selector__label">Select one</span>
      <RadioGroup
        variant="outlined"
        fullWidth
        value={selectedMethod}
        onChange={(e: any) => onSelect(e.target.value)}
        aria-label="Select payment method"
      >
        {availableMethods.map((method) => (
          <Radio
            key={method.id}
            value={method.id}
            id={method.id}
            label={renderLabel(method) as any}
          />
        ))}
      </RadioGroup>
    </>
  );
};

export default PaymentMethodSelector;
